import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { faCommentSmile } from "@fortawesome/pro-solid-svg-icons/faCommentSmile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFlows } from "@frigade/react";
import Affix from "antd/es/affix";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";

import { Role } from "@mapmycustomers/shared/enum";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Button } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { changeScope } from "@app/scene/dashboard/store/actions";
import { getCurrentUser } from "@app/store/iam";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Scope from "@app/types/dashboard/Scope";
import frigadeService from "@app/util/frigade/frigadeService";

import DashboardContent from "./components/DashboardContent";
import Header from "./components/Header";
import DashboardTabs from "./components/Header/components/DashboardTabs";
import Sidebar from "./components/Sidebar";
import TimelineContent from "./components/TimelineContent";
import styles from "./DashboardPage.module.scss";

interface Props {
  currentUser: User;
  onScopeChange?: (payload: { scope: Scope; view: ViewType }) => void;
  teams: Team[];
}

export const DashboardPage: React.FC<Props> = ({ currentUser, onScopeChange, teams }) => {
  const intl = useIntl();
  const history = useHistory();
  const params = useParams<{ teamId?: string; userId?: string; viewType?: string }>();
  const userId = params.userId ? parseInt(params.userId, 10) : undefined;
  const teamId = params.teamId ? parseInt(params.teamId, 10) : undefined;
  const view: ViewType =
    params.viewType && Object.values(ViewType).includes(params.viewType as ViewType)
      ? (params.viewType as ViewType)
      : ViewType.DASHBOARD;

  useEffect(() => {
    // Before switching Scopes, check that current user has access to it and redirect
    // to the one they have access to if not
    let finalTeamId = teamId;
    let finalUserId = userId;

    switch (currentUser.role.key) {
      case Role.MEMBER:
      case Role.READ_ONLY: {
        // reps can only see their own board, so make sure userId is always their id
        if (userId !== currentUser.id) {
          finalUserId = currentUser.id;
          finalTeamId = currentUser.teams[0]?.id;
        }
        break;
      }
      case Role.MANAGER: {
        // check that team is one of manager teams and that user they selected also belongs to
        // one of these teams
        if (
          !teams.some(({ id }) => id === teamId) ||
          (!!userId &&
            userId !== currentUser.id &&
            !teams.some(({ users }) => users.some(({ id }) => id === userId)))
        ) {
          finalUserId = currentUser.id;
          finalTeamId = currentUser.teams[0]?.id;
        }
        break;
      }
    }

    // if scope was corrected, update the url too
    if (finalTeamId !== teamId || finalUserId !== userId) {
      const url = `${Path.DASHBOARD}${finalTeamId ? `/teams/${finalTeamId}` : ""}${
        finalUserId ? `/users/${finalUserId}` : ""
      }${view !== ViewType.DASHBOARD ? `/${view}` : ""}`;
      history.replace(url);
    }

    onScopeChange?.({ scope: { teamId: finalTeamId, userId: finalUserId }, view });
  }, [currentUser, onScopeChange, teamId, userId, view, teams, history]);

  const scope = useMemo(() => ({ teamId, userId }), [userId, teamId]);

  const { markFlowNotStarted } = useFlows();
  const handleFeedbackClick = useDynamicCallback(async () => {
    const slug = "flow_973nMGdX";
    await markFlowNotStarted(slug);
    frigadeService.showFlow(slug);
  });

  return (
    <div className={styles.container}>
      <Sidebar activeScope={scope} className={styles.scopes} view={view} />
      <div className={styles.main}>
        <Header scope={scope} view={view} />
        <DashboardTabs view={view} />
        {view === ViewType.DASHBOARD && (
          <DashboardContent className={styles.content} scope={scope} />
        )}
        {view === ViewType.TIMELINE && <TimelineContent className={styles.content} scope={scope} />}
        <Affix className={styles.feedbackContainer} offsetBottom={13}>
          <Button
            className={styles.feedbackButton}
            onClick={handleFeedbackClick}
            size="large"
            type="primary"
          >
            {intl.formatMessage(
              {
                id: "dashboard.feedbackButton",
                defaultMessage: "We want your feedback {icon}",
                description: "Tooltip for feedback button on the Dashboard Page",
              },
              {
                icon: <FontAwesomeIcon icon={faCommentSmile} />,
              }
            )}
          </Button>
        </Affix>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: getCurrentUser(state)!,
  teams: getTeams(state),
});

const mapDispatchToProps = {
  onScopeChange: changeScope.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPage);
